"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Input } from "@/components/ui/input";

type SearchHit = {
  id: number;
  title: string;
  slug: string;
  excerpt?: string | null;
  category?: { slug: string; title: string };
};

export function HelpSearch() {
  const t = useTranslations("help");
  const locale = useLocale();
  const router = useRouter();

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchHit[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `/api/help/search?q=${encodeURIComponent(trimmed)}&locale=${locale}`,
          { signal: controller.signal }
        );
        const data = await res.json();
        setResults(data.results ?? []);
        setOpen(true);
      } catch (err) {
        if ((err as { name?: string }).name !== "AbortError") {
          console.error("[help-search]", err);
        }
      } finally {
        setLoading(false);
      }
    }, 200);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [query, locale]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;
    router.push(`/${locale}/help/search?q=${encodeURIComponent(trimmed)}`);
    setOpen(false);
  }

  return (
    <div ref={wrapperRef} className="relative w-full">
      <form onSubmit={handleSubmit} className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted dark:text-muted-dark" />
        <Input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder={t("searchPlaceholder")}
          aria-label={t("searchPlaceholder")}
          className="pl-9"
        />
      </form>

      {open && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-96 overflow-y-auto rounded-md border border-neutral-200 bg-white shadow-lg dark:border-neutral-800 dark:bg-neutral-950">
          {loading && results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-muted dark:text-muted-dark">…</div>
          ) : results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-muted dark:text-muted-dark">
              {t("noResultsFor", { query })}
            </div>
          ) : (
            <ul>
              {results.map((hit) => (
                <li key={hit.id} className="border-b border-neutral-100 last:border-0 dark:border-neutral-800">
                  <Link
                    href={`/${locale}/help/article/${hit.slug}`}
                    className="block px-4 py-2.5 text-sm transition-colors hover:bg-neutral-50 dark:hover:bg-neutral-900/60"
                    onClick={() => setOpen(false)}
                  >
                    <div className="font-medium text-neutral-900 dark:text-white">{hit.title}</div>
                    {hit.category && (
                      <div className="mt-0.5 text-xs text-muted dark:text-muted-dark">
                        {hit.category.title}
                      </div>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
